import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { useAuth, useCharacterQuery, useSystems, useUndoRedo } from "../api/queries";
import type { CharacterView, MessageView, TermLanguage, ValidationMessage } from "../api/types";
import { CharacterProvider, useCharacter } from "../character";
import { MessageList } from "../components/MessageList";
import { useCharacterScroll, useSwipeToNeighbour, useTrackOpenCharacter } from "../components/OpenCharacters";
import { OpenPointsSheet } from "../components/OpenPoints";
import { sheetTabsFor, systemUi } from "../registry";
import { text } from "../text";

export function CharacterLayout() {
  const { id = "" } = useParams();
  const character = useCharacterQuery(id);
  const systems = useSystems();
  const auth = useAuth();
  if (character.isLoading) return <p className="muted">Lade …</p>;
  if (character.error || !character.data) return <p className="error">Charakter nicht gefunden.</p>;
  const language = (auth.data?.user?.termLanguage ?? "de") as TermLanguage;
  return (
    <CharacterProvider character={character.data} system={systems.data?.find((s) => s.id === character.data.system)} language={language}>
      <CharacterFrame />
    </CharacterProvider>
  );
}

function CharacterFrame() {
  const { character, error, clearError, language } = useCharacter();
  const { undo, redo } = useUndoRedo(character.id);
  useTrackOpenCharacter(character.id, !character.readOnly);
  useCharacterScroll(character.id);
  const swipe = useSwipeToNeighbour(character.id);
  const ui = systemUi(character.system);
  const location = useLocation();
  const navigate = useNavigate();
  const importMessages = (location.state as { importMessages?: ValidationMessage[] } | null)?.importMessages ?? [];
  const tabs = sheetTabsFor(ui, character.mode);
  const base = `/c/${character.id}`;

  return (
    <div className="character" {...swipe}>
      <header className="character-header">
        <div>
          <h1>{character.name}</h1>
          <p className="muted">
            {character.evaluation.summary.subtitle}
            {character.evaluation.summary.level && <> · {character.evaluation.summary.level}</>}
          </p>
        </div>
        <div className="character-status">
          <CharacterBadges />
          {character.readOnly && <span className="badge">nur lesend</span>}
          {!character.readOnly && (
            <>
          <button
            type="button"
            className="icon-button"
            disabled={!character.undoSummary || undo.isPending}
            title={character.undoSummary ? `Rückgängig: ${character.undoSummary}` : (character.undoRefusal ?? "Nichts rückgängig zu machen")}
            aria-label="Rückgängig"
            onClick={() => undo.mutate()}
          >
            ↶
          </button>
          <button
            type="button"
            className="icon-button"
            disabled={!character.redoSummary || redo.isPending}
            title={character.redoSummary ? `Wiederholen: ${character.redoSummary}` : (character.redoRefusal ?? "Nichts zu wiederholen")}
            aria-label="Wiederholen"
            onClick={() => redo.mutate()}
          >
            ↷
          </button>
            </>
          )}
        </div>
      </header>

      <nav className="tabs" aria-label="Charakterbereiche">
        {character.mode === "creation" && (ui?.wizardSteps || character.evaluation.steps.length > 0) && <NavLink to={`${base}/assistent`}>Assistent</NavLink>}
        {character.mode === "career" && <NavLink to={`${base}/spiel`}>Spiel</NavLink>}
        {tabs.map((tab) => (
          <NavLink key={tab.id} to={`${base}/bogen/${tab.id}`}>
            {tab.label}
          </NavLink>
        ))}
        <NavLink to={`${base}/werte`}>Werte</NavLink>
        <NavLink to={`${base}/protokoll`}>Protokoll</NavLink>
        <NavLink to={`${base}/konto`}>Konto</NavLink>
        {!character.readOnly && <NavLink to={`${base}/einstellungen`}>Einstellungen</NavLink>}
      </nav>
      {character.readOnly && (
        <p className="notice" role="status">
          Charakter von {character.ownerName} aus der Gruppe deiner Kampagne, nur zum Lesen. Ändern kann ihn nur sein Besitzer; die Belohnung eines Abschlusses bucht die Kampagne.
          {character.canAcknowledge && " Hinweisen zur Zustimmung stimmst du hier zu."}
        </p>
      )}
      {character.readOnly && character.canAcknowledge && <GameMasterApprovals />}

      {importMessages.length > 0 && (
        <div className="card warning-box">
          <h3>Hinweise zum Import</h3>
          <ul>
            {importMessages.map((m, i) => (
              <li key={i}>{text(m.text, language)}</li>
            ))}
          </ul>
          <button type="button" className="link" onClick={() => navigate(location.pathname, { replace: true, state: null })}>
            Ausblenden
          </button>
        </div>
      )}
      {(error || undo.error || redo.error) && (
        <div className="error-banner" role="alert">
          {error?.message ?? undo.error?.message ?? redo.error?.message}
          {error?.messages.map((m, i) => <div key={i}>{text(m.text, language)}</div>)}
          <button type="button" className="link" onClick={clearError}>
            Schließen
          </button>
        </div>
      )}
      {character.readOnly ? (
        // Disabled form controls keep every change out of reach, also those the modules' own pages offer.
        <fieldset className="read-only" disabled>
          <Outlet />
        </fieldset>
      ) : (
        <Outlet />
      )}
    </div>
  );
}

/** The hints a game master of the party may approve on a character shared with the campaign, outside the disabled sheet (E51). */
function GameMasterApprovals() {
  const { character, apply } = useCharacter();
  const messages = character.evaluation.messages.filter((m) => m.message.acknowledgeable);
  if (messages.length === 0) return null;
  return (
    <section className="card" aria-label="Zustimmung der Spielleitung">
      <h3>Zustimmung der Spielleitung</h3>
      <MessageList
        messages={messages}
        compact
        approvals
        onAcknowledge={(m: MessageView) => void apply({ type: "core.acknowledge", payload: { key: m.key } })}
        onUnacknowledge={(m: MessageView) => void apply({ type: "core.unacknowledge", payload: { key: m.key } })}
      />
    </section>
  );
}

/** Mode of the character, with the number of errors and of required choices still open; tapping either shows them. */
export function CharacterBadges() {
  const { character } = useCharacter();
  const [showing, setShowing] = useState(false);
  const errors = character.evaluation.messages.filter((m) => m.message.severity === "error").length;
  const open = character.evaluation.openChoices.filter((c) => c.required).length;
  return (
    <>
      <span className={`badge mode-${character.mode}`}>{character.mode === "creation" ? "Erschaffungsmodus" : "Karrieremodus"}</span>
      {errors > 0 && (
        <button type="button" className="badge error" aria-haspopup="dialog" title="Fehler und offene Punkte anzeigen" onClick={() => setShowing(true)}>
          {errors} Fehler
        </button>
      )}
      {open > 0 && (
        <button type="button" className="badge warning" aria-haspopup="dialog" title="Fehler und offene Punkte anzeigen" onClick={() => setShowing(true)}>
          {open} offen
        </button>
      )}
      {showing && <OpenPointsSheet onClose={() => setShowing(false)} />}
    </>
  );
}

/** The page a character opens on: the assistant or the play page of its mode, else its first sheet tab. */
function homePath(character: CharacterView): string {
  const ui = systemUi(character.system);
  const firstTab = sheetTabsFor(ui, character.mode)[0];
  return character.mode === "creation" && (ui?.wizardSteps || character.evaluation.steps.length > 0)
    ? "assistent"
    : character.mode === "career"
      ? "spiel"
      : firstTab
        ? `bogen/${firstTab.id}`
        : "werte";
}

export function CharacterHome() {
  const { character } = useCharacter();
  return <RedirectTo to={`/c/${character.id}/${homePath(character)}`} />;
}

/** A sheet tab; one that belongs to the other mode, e.g. remembered before an undone mode change, gives way to the first tab of this mode. */
export function SheetTabPage() {
  const { tab = "" } = useParams();
  const { character } = useCharacter();
  const ui = systemUi(character.system);
  const sheetTab = ui?.sheetTabs.find((t) => t.id === tab);
  if (!sheetTab) return <p className="muted">Diesen Bereich gibt es nicht.</p>;
  const tabs = sheetTabsFor(ui, character.mode);
  if (!tabs.includes(sheetTab)) {
    const target = tabs[0] ? `bogen/${tabs[0].id}` : homePath(character);
    return <RedirectTo to={`/c/${character.id}/${target}`} />;
  }
  const Component = sheetTab.component;
  return <Component />;
}

function RedirectTo(props: { to: string }) {
  const navigate = useNavigate();
  queueMicrotask(() => navigate(props.to, { replace: true }));
  return null;
}
